import { useContext } from 'react';
import { Formik } from 'formik';
import { useParams } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { X } from '@phosphor-icons/react';
import { Stack, Switch } from '@mui/material';
import { api } from '../../services/api';
import { LoadingContext } from '../../providers/loading';
import { UserDataContext } from '../../providers/userData';
import { useTheme } from '../../providers/theme';
import { OpenContext } from '../../providers/openModal';
import { Field } from '../../types/FieldType';
import { FIELD_TYPE } from '../../FieldTypes';
import { TextComponent } from '../TextComponent';
import { InputComponent } from '../InputComponent';
import { Button } from '../Button';
import { ImageFieldModalSchema } from './ImageModalSchema';
import { BoxImageModal, ContentModal, Footer, HeaderModal, InputContainer, ModalContainer, SwitchContainer } from './styles';
import { useTranslation } from 'react-i18next';

interface ImageFieldModalType {
  dataField: Field;
  isEditFieldModalOpen: boolean;
  getProjectData: () => Promise<void>;
  fieldsLength: number;
}

export function ImageFieldModal({ dataField, isEditFieldModalOpen, getProjectData, fieldsLength }: ImageFieldModalType) {
  const { projectId } = useParams();
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const { userData } = useContext(UserDataContext);
  const { isOpen, setIsOpen } = useContext(OpenContext);
  const { theme } = useTheme();
  const imageFieldType = FIELD_TYPE.image;
  const { t } = useTranslation();

  async function createFieldData(values: { title: string; description: string; isRequired: boolean }) {
    setIsLoading({ value: true });
    setIsOpen((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        image: false
      }
    }));
    await api.post(`/project/${projectId}/field`, {
      fields: [
        {
          owner_id: userData.userEmail,
          title: values.title,
          description: values.description,
          order: fieldsLength,
          is_active: true,
          is_file: false,
          is_required: values.isRequired,
          project_id: projectId,
          field_type_id: imageFieldType,
          field_options: []
        }
      ]
    })
      .then(async () => {
        await getProjectData();
        setIsLoading({ value: false });
      })
      .catch(error => console.log(error));
  }

  async function updateFieldData(fieldId: string, values: { title: string; description: string; isRequired: boolean | undefined }) {
    setIsLoading({ value: true });
    setIsOpen((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        image: false
      }
    }));
    await api.put(`/project/${projectId}/field?id=${fieldId}`,
      {
        owner_id: userData.userEmail,
        title: values.title,
        description: values.description,
        is_active: true,
        is_file: false,
        is_required: values.isRequired,
        project_id: projectId,
        field_type_id: imageFieldType,
        field_options: []
      }
    )
      .then(async () => {
        await getProjectData();
        setIsLoading({ value: false });
      })
      .catch(error => console.log(error));
  }

  function toggle() {
    setIsOpen((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        image: false
      }
    }));
  }

  return (
    <>
      {isOpen.values.image && !isEditFieldModalOpen && (
        <ModalContainer>
          <BoxImageModal>
            <HeaderModal>
              <span style={{ flexGrow: 1, textAlign: 'center' }}>NOVA IMAGEM</span>
              <X onClick={() => toggle()} size={25} color={theme.colors.red} weight='bold' cursor='pointer' />
            </HeaderModal>

            <Formik
              initialValues={{
                title: '',
                description: '',
                owner_id: userData.userEmail || '',
                isRequired: false
              }}
              validationSchema={ImageFieldModalSchema}
              validateOnChange={true}
              validateOnMount={true}
              onSubmit={values => createFieldData(values)}
            >
              {({ values, touched, errors, handleSubmit, handleChange }) => (
                <form onSubmit={handleSubmit}>
                  <ContentModal>
                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('button-project-field-name')}
                      </TextComponent>
                      <InputComponent
                        id='title'
                        type='text'
                        value={values.title}
                        onChange={handleChange}
                        placeholder={t('title-placeholder')}
                      />
                      {errors.title && touched.title ? (
                        <div>
                          <span style={{ fontSize: '12px' }}>{errors.title}</span>
                        </div>
                      ) : null}
                    </InputContainer>

                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('button-project-field-description')}
                      </TextComponent>
                      <InputComponent
                        id='description'
                        type='text'
                        value={values.description}
                        onChange={handleChange}
                        placeholder={t('write-text-placeholder')}
                      />
                      {errors.description && touched.description ? (
                        <div>
                          <span style={{ fontSize: '12px' }}>{errors.description}</span>
                        </div>
                      ) : null}
                    </InputContainer>

                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('button-project-field-manager')}
                      </TextComponent>
                      <InputComponent
                        id='fieldOwnerId'
                        type='text'
                        value={userData.userEmail}
                        readOnly
                      />
                    </InputContainer>

                    <Footer>
                      <SwitchContainer>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Switch id='isRequired' name='isRequired' value={values.isRequired} onChange={handleChange} color='warning' />
                          <span>{t('obligatory-field')}</span>
                        </Stack>
                      </SwitchContainer>

                      <Button
                        id='save'
                        type='submit'
                        color={theme.colors.main}
                        sxhover={theme.colors.hoverMain}
                        sxmargintop='20px'
                      >
                        {isLoading.value ? (
                          <Oval color='#fff' secondaryColor='#fff' height='20px' />
                        ) : t('group-page-button')}
                      </Button>
                    </Footer>
                  </ContentModal>
                </form>
              )}
            </Formik>
          </BoxImageModal>
        </ModalContainer >
      )}

      {
        isOpen.values.image && isEditFieldModalOpen && (
          <ModalContainer>
            <BoxImageModal>
              <HeaderModal>
                <span style={{ flexGrow: 1, textAlign: 'center' }}>{isEditFieldModalOpen === true ? dataField?.title : 'NOVO CAMPO'}</span>
                <X onClick={() => toggle()} size={25} color={theme.colors.red} weight='bold' cursor='pointer' />
              </HeaderModal>

              <Formik
                initialValues={{
                  title: dataField?.title,
                  description: dataField?.description,
                  owner_id: userData.userEmail || '',
                  isRequired: dataField?.is_required === null ? false : dataField?.is_required
                }}
                validationSchema={ImageFieldModalSchema}
                validateOnChange={true}
                validateOnMount={true}
                onSubmit={values => updateFieldData(dataField?.id, values)}
              >
                {({ values, touched, errors, handleSubmit, handleChange }) => (
                  <form onSubmit={handleSubmit}>
                    <ContentModal>
                      <InputContainer>
                        <TextComponent
                          fontWeight='bold'
                          fontSize='14px'
                        >
                          {t('button-project-field-name')}
                        </TextComponent>
                        <InputComponent
                          id='title'
                          type='text'
                          value={values.title}
                          onChange={handleChange}
                          placeholder={t('title-placeholder')}
                        />
                        {errors.title && touched.title ? (
                          <div>
                            <span style={{ fontSize: '12px' }}>{errors.title}</span>
                          </div>
                        ) : null}
                      </InputContainer>

                      <InputContainer>
                        <TextComponent
                          fontWeight='bold'
                          fontSize='14px'
                        >
                          {t('button-project-field-description')}
                        </TextComponent>
                        <InputComponent
                          id='description'
                          type='text'
                          value={values.description}
                          onChange={handleChange}
                          placeholder={t('write-text-placeholder')}
                        />
                        {errors.description && touched.description ? (
                          <div>
                            <span style={{ fontSize: '12px' }}>{errors.description}</span>
                          </div>
                        ) : null}
                      </InputContainer>

                      <InputContainer>
                        <TextComponent
                          fontWeight='bold'
                          fontSize='14px'
                        >
                          {t('button-project-field-manager')}
                        </TextComponent>
                        <InputComponent
                          id='fieldOwnerId'
                          type='text'
                          value={userData.userEmail}
                          readOnly
                        />
                      </InputContainer>

                      <Footer>
                        <SwitchContainer>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Switch
                              id='isRequired'
                              name='isRequired'
                              value={values.isRequired}
                              onChange={handleChange}
                              checked={values.isRequired ? true : false}
                              color='warning'
                            />
                            <span>{t('obligatory-field')}</span>
                          </Stack>
                        </SwitchContainer>

                        <Button
                          id='save'
                          type='submit'
                          color={theme.colors.main}
                          sxhover={theme.colors.hoverMain}
                          sxmargintop='20px'
                        >
                          {isLoading.value ? (
                            <Oval color='#fff' secondaryColor='#fff' height='20px' />
                          ) : 'ATUALIZAR'}
                        </Button>
                      </Footer>
                    </ContentModal>
                  </form>
                )}
              </Formik>
            </BoxImageModal>
          </ModalContainer >
        )
      }
    </>
  );
}
